import BaseModel3d from '../BaseModel3d';
import { globalStores } from '@/editor/stores';
import { pixelRatio3d } from '@/editor/math/const';
import { IBaseDataHoleFace, IFaceData } from '@/editor/data/face/face';
import { EEntityType, IPureEdge } from '@/editor/types';
import { EContainerType } from '@/editor/types/entity';
import { BufferAttribute, BufferGeometry, Mesh } from 'three';
import { createMaterial } from '@/editor/data/material/utils';
import { IPoint2d } from '@/editor/math/IPoint2d';
import { Point2dUtils } from '@/editor/math/Point2dUtils';
import { getPathFromEdges } from '@/editor/utils/edge';
import { createPlaneGeometry } from '../../plane/utils';
import { flipGeometry } from '../../utils';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { getArcEdgesByRectBox } from '@/editor/utils/arc';

export class ArcHoleFaceModel extends BaseModel3d {
  constructor(options: { id: string }) {
    super({ id: options.id });

    this.model.userData.type = EEntityType.Face;
    this.model.userData.containerType = EContainerType.entity;
    this.model.userData.entityId = this._id;

    globalStores.editorStateStore.sceneInfo.scene3d.intersectableGroup.add(this.model);
  }

  createModel(faceData: IFaceData) {
    if (!this.canUpdate) return;

    this.clearModel();

    const mesh = this.createArcPlane(faceData);
    if (!mesh) return;

    this.model.add(mesh);
  }

  updateWithoutDestroy(_faceData: IFaceData) {}

  private createArcPlane(faceData: IFaceData) {
    const { center, width, height, offGround, arcCenter, thickness } = faceData.baseData as IBaseDataHoleFace;
    if (!arcCenter) return;

    const edges = getArcEdgesByRectBox({ rectCenter: center, rectWidth: width, arcCenter, thickness });
    if (!edges) return;

    // 底部
    const bottomPath = getPathFromEdges(edges);
    const bottomGeometry = createPlaneGeometry(bottomPath);
    bottomGeometry.translate(0, 0, offGround / pixelRatio3d);

    // 顶部
    const topGeometry = bottomGeometry.clone();
    flipGeometry(topGeometry); // 翻转
    topGeometry.translate(0, 0, height / pixelRatio3d);

    // 左右侧
    const [, rightEdge, , leftEdge] = edges;
    const leftGeometry = this.createLeftSideGeometry({ leftEdge, offGround, height, arcCenter });
    const rightGeometry = this.createRightSideGeometry({ rightEdge, offGround, height, arcCenter });

    const mergedGeometry = mergeGeometries([bottomGeometry, topGeometry, leftGeometry, rightGeometry]);
    const material = createMaterial(faceData.materialInfo.id);

    return new Mesh(mergedGeometry, material);
  }

  private createLeftSideGeometry(params: {
    leftEdge: IPureEdge;
    offGround: number;
    height: number;
    arcCenter: IPoint2d;
  }) {
    const { leftEdge, offGround, height, arcCenter } = params;
    const radialDirection = Point2dUtils.getUnitDirection(arcCenter, leftEdge.start);
    const leftDirection = Point2dUtils.rotateDirection(radialDirection, -Math.PI / 2);
    const leftGeometry = createSideQuadGeometry({
      start: leftEdge.start,
      end: leftEdge.end,
      offGround,
      height,
      normal: leftDirection
    });

    return leftGeometry;
  }
  private createRightSideGeometry(params: {
    rightEdge: IPureEdge;
    offGround: number;
    height: number;
    arcCenter: IPoint2d;
  }) {
    const { rightEdge, offGround, height, arcCenter } = params;
    const radialDirection = Point2dUtils.getUnitDirection(arcCenter, rightEdge.start);
    const rightDirection = Point2dUtils.rotateDirection(radialDirection, Math.PI / 2);

    const rightGeometry = createSideQuadGeometry({
      start: rightEdge.start,
      end: rightEdge.end,
      offGround,
      height,
      normal: rightDirection
    });

    return rightGeometry;
  }
}

// 构造一个 indexed 的侧面四边形 geometry,与 ShapeGeometry 的属性集合保持一致(position/normal/uv + index)
function createSideQuadGeometry(params: {
  start: IPoint2d;
  end: IPoint2d;
  offGround: number;
  height: number;
  normal: IPoint2d;
}) {
  const { start, end, offGround, height, normal } = params;
  const r = pixelRatio3d;

  // 4 个唯一顶点:0=start底, 1=end底, 2=end顶, 3=start顶
  const positions = new Float32Array([
    start.x / r,
    start.y / r,
    offGround / r,
    end.x / r,
    end.y / r,
    offGround / r,
    end.x / r,
    end.y / r,
    (offGround + height) / r,
    start.x / r,
    start.y / r,
    (offGround + height) / r
  ]);

  const normals = new Float32Array([
    normal.x,
    normal.y,
    0,
    normal.x,
    normal.y,
    0,
    normal.x,
    normal.y,
    0,
    normal.x,
    normal.y,
    0
  ]);

  const uvs = new Float32Array([0, 0, 1, 0, 1, 1, 0, 1]);

  // 两个三角形:0-1-2, 0-2-3
  const indices = new Uint16Array([0, 1, 2, 0, 2, 3]);

  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new BufferAttribute(positions, 3));
  geometry.setAttribute('normal', new BufferAttribute(normals, 3));
  geometry.setAttribute('uv', new BufferAttribute(uvs, 2));
  geometry.setIndex(new BufferAttribute(indices, 1));
  return geometry;
}
